/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 *
 * Roles aligned with Clerk organization roles:
 * Admin, Estimator, Manager, Owner (Default & creator), Viewer, Crew Leader
 */

export type AppRole =
  | "OWNER"
  | "ADMIN"
  | "MANAGER"
  | "SALESMAN"
  | "ESTIMATOR"
  | "CREW_LEADER"
  | "LABORER"
  | "VIEWER";

const VALID_ROLES: readonly AppRole[] = [
  "OWNER",
  "ADMIN",
  "MANAGER",
  "SALESMAN",
  "ESTIMATOR",
  "CREW_LEADER",
  "LABORER",
  "VIEWER",
] as const;

export function normalizeRole(value: unknown): AppRole {
  const raw = String(value || "")
    .toUpperCase()
    .replace(/^ORG:/, "");
  if (VALID_ROLES.includes(raw as AppRole)) {
    return raw as AppRole;
  }
  if (raw === "MEMBER") {
    return "VIEWER";
  }
  return "VIEWER";
}

export const DATA_WRITE_ROLES: readonly AppRole[] = [
  "OWNER",
  "ADMIN",
  "MANAGER",
  "SALESMAN",
  "ESTIMATOR",
] as const;

export const FIELD_OPS_WRITE_ROLES: readonly AppRole[] = [
  ...DATA_WRITE_ROLES,
  "CREW_LEADER",
] as const;

export const CLOCK_ROLES: readonly AppRole[] = [
  ...FIELD_OPS_WRITE_ROLES,
  "LABORER",
] as const;

export function permissionsForRole(role: AppRole): {
  canManageOrganization: boolean;
  canManageUsers: boolean;
  canManageData: boolean;
  canViewSensitive: boolean;
  canManageTeam: boolean;
  canManageFieldOps: boolean;
} {
  const canManageUsers = role === "OWNER" || role === "ADMIN";
  const canManageOrganization = canManageUsers;
  const canManageTeam =
    role === "OWNER" || role === "ADMIN" || role === "MANAGER";
  const canManageFieldOps = canManageTeam || role === "CREW_LEADER";
  return {
    canManageOrganization,
    canManageUsers,
    canManageData: (DATA_WRITE_ROLES as readonly string[]).includes(role),
    canViewSensitive: role === "OWNER" || role === "ADMIN",
    canManageTeam,
    canManageFieldOps,
  };
}

// ============================================================================
// SENSITIVE FIELD FILTERING
// ============================================================================

/**
 * Fields that contain sensitive financial data.
 * When canViewSensitive is false, these are redacted from API responses.
 */
const SENSITIVE_FIELDS = new Set([
  "margin_percentage",
  "margin_rate",
  "markup_percentage",
  "markup",
  "burden_rate",
  "profit_percentage",
  "profit_amount",
  "overhead_amount",
  "overhead_percentage",
  "unit_cost",
  "total_cost",
  "cost",
  "labor_cost",
  "material_cost",
  "equipment_cost",
  "cost_breakdown",
  "calculation_breakdown",
]);

/**
 * Strip sensitive financial fields from an API response object.
 * Returns a shallow copy with sensitive fields removed.
 * Works on single objects or arrays.
 */
export function stripSensitiveFields<T>(data: T, canViewSensitive: boolean): T {
  if (canViewSensitive) return data;

  if (Array.isArray(data)) {
    return data.map((item) =>
      stripSensitiveFields(item, false),
    ) as unknown as T;
  }

  if (data && typeof data === "object") {
    const filtered: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(data)) {
      if (!SENSITIVE_FIELDS.has(key)) {
        filtered[key] = value;
      }
    }
    return filtered as T;
  }

  return data;
}

// ============================================================================
// SERVER ACTION ROLE VALIDATION
// ============================================================================

/**
 * Validate that a server action caller has the required role.
 * Throws if the role is missing or insufficient.
 *
 * Usage:
 *   const ctx = await getServerActionContext();
 *   requireRole(ctx.userRole, DATA_WRITE_ROLES);
 */
export function requireRole(
  userRole: string | null | undefined,
  allowedRoles: readonly AppRole[],
): void {
  if (!userRole) {
    throw new Error("Unauthorized: User role not found");
  }
  const normalized = normalizeRole(userRole);
  if (!(allowedRoles as readonly string[]).includes(normalized)) {
    throw new Error(
      `Forbidden: Role '${normalized}' cannot perform this action`,
    );
  }
}
